import React, { useEffect, useRef, useState } from 'react'
import { TextInput, Grid, HelperText } from 'gu-ui-kit'
import clsx from 'clsx'
import styles from 'components/FormControls/form-controls.module.scss'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'

const renderNumericSegmentedField = ({
  input,
  meta: { touched, error, submitting },
  InputProps: { className = '', ...inputProps } = {},
  type = 'text',
  disabled,
  helperText,
  name,
  length = 6,
  t,
  autoFocus = false,
  ...rest
}) => {
  const [values, setValues] = useState(Array(length).fill(''))
  const inputsRef = useRef({})
  const breakpoint = useBreakpoint()

  const handleChange = (event, index) => {
    const { value } = event.target

    if (/^\d*$/.test(value) && value.length <= 1) {
      const newValues = [...values]
      newValues[index] = value
      setValues(newValues)
      input.onChange(newValues.join(''))

      if (value && index < length - 1) {
        inputsRef.current[index + 1].focus()
      } else if (value && index === length - 1) {
        inputsRef.current[index].blur()
      }
    }
  }

  const handleKeyDown = (event, index) => {
    if (event.key === 'Backspace' && !event.target.value && index > 0) {
      inputsRef.current[index - 1].focus()
    } else if (event.target.value.length && /^\d*$/.test(event.key)) {
      const newValues = [...values]
      newValues[index] = ''
      setValues(newValues)
    }
  }

  const handlePaste = (event, index) => {
    const pastedData = event.clipboardData.getData('text/plain')
    event.preventDefault()

    const numbersOnly = pastedData.replace(/\D/g, '').slice(0, length - index)
    const newSegments = numbersOnly.split('')
    const newValues = [...values]

    newSegments.forEach((value, i) => {
      newValues[index + i] = value
    })

    setValues(newValues)
    input.onChange(newValues.join(''))

    if (index + newSegments.length < length) {
      inputsRef.current[index + newSegments.length].focus()
    } else {
      inputsRef.current[length - 1].blur()
    }
  }

  useEffect(() => {
    const inputValue = input.value.split('')
    setValues((prevValues) => prevValues.map((_, index) => inputValue?.[index] ?? ''))
  }, [input])

  useEffect(() => {
    if (autoFocus) {
      inputsRef.current[0].focus()
    }
  }, [autoFocus])

  return (
    <Grid
      direction={'column'}
      gap={8}
    >
      <Grid
        wrap={false}
        direction={'row'}
        gap={breakpoint.xs ? 13 : breakpoint.sm ? 17 : 21}
      >
        {values.map((value, index) => (
          <TextInput
            key={index}
            maxLength={1}
            ref={(el) => (inputsRef.current[index] = el)}
            fullWidth
            value={value}
            {...rest}
            error={!!(touched && error)}
            type={type}
            disabled={submitting || disabled}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={(e) => handlePaste(e, index)}
            InputProps={{
              ...inputProps,
              className: clsx(className, styles['numeric-segmented-control']),
              name,
            }}
          />
        ))}
      </Grid>
      <HelperText error={touched && error}>{touched && error ? t(error) : helperText}</HelperText>
    </Grid>
  )
}

export default renderNumericSegmentedField
